import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import './Select.css';

export interface SelectOption {
    value: string;
    label: string;
}

interface SelectProps {
    value: string;
    onChange: (value: string) => void;
    options: SelectOption[];
    placeholder?: string;
    disabled?: boolean;
    label?: string;
}

const Select: React.FC<SelectProps> = ({
    value,
    onChange,
    options,
    placeholder = 'Selecione uma opção',
    disabled = false,
    label
}) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const inputContainerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
    const [searchTerm, setSearchTerm] = useState('');

    const updateDropdownPosition = useCallback(() => {
        if (inputContainerRef.current) {
            const rect = inputContainerRef.current.getBoundingClientRect();
            const isMobile = window.innerWidth <= 768;
            const dropdownWidth = rect.width;
            const viewportWidth = window.innerWidth;
            let leftPosition = rect.left;
            if (leftPosition + dropdownWidth > viewportWidth) {
                leftPosition = Math.max(16, viewportWidth - dropdownWidth - 16);
            }
            
            setDropdownStyle({
                position: 'fixed',
                top: rect.bottom + 4,
                left: leftPosition,
                width: rect.width,
                maxWidth: isMobile ? 'calc(100vw - 2rem)' : 'none',
                zIndex: 2000,
            });
        }
    }, []);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            if (
                wrapperRef.current && 
                !wrapperRef.current.contains(target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(target)
            ) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };
        
        const handleResize = () => {
            if (isOpen) {
                updateDropdownPosition();
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleResize, true);
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleResize, true);
        };
    }, [isOpen, updateDropdownPosition]);

    const toggleDropdown = () => {
        if (!disabled) {
            if (!isOpen) updateDropdownPosition();
            setIsOpen(prev => !prev);
            setSearchTerm('');
        }
    };

    const handleSelect = (optionValue: string) => {
        onChange(optionValue);
        setIsOpen(false);
        setSearchTerm('');
    };

    const selectedOption = options.find(opt => opt.value === value);
    const displayValue = selectedOption ? selectedOption.label : placeholder;

    const filteredOptions = options.filter(option =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const dropdown = isOpen ? (
        <div className="select-dropdown" style={dropdownStyle} ref={dropdownRef}>
            <div className="select-search">
                <input
                    type="text"
                    className="select-search-input"
                    placeholder="Pesquisar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus
                />
            </div>
            <div className="select-options">
                {filteredOptions.length === 0 ? (
                    <div className="select-option-empty">Nenhuma opção encontrada</div>
                ) : (
                    filteredOptions.map((option) => (
                        <div
                            key={option.value}
                            className={`select-option ${option.value === value ? 'selected' : ''}`}
                            onClick={() => handleSelect(option.value)}
                            title={option.label}
                        >
                            {option.label}
                        </div>
                    ))
                )}
            </div>
        </div>
    ) : null;

    return (
        <div className="select-container">
            {label && <label className="select-label">{label}</label>}
            <div className="select-wrapper" ref={wrapperRef}>
                <div 
                    className={`select-input-container ${disabled ? 'disabled' : ''}`}
                    ref={inputContainerRef}
                    onClick={toggleDropdown}
                >
                    <div className={`select-input ${!selectedOption ? 'placeholder' : ''}`}>
                        {displayValue}
                    </div>
                    <FontAwesomeIcon
                        icon={faChevronDown}
                        className={`select-icon ${isOpen ? 'open' : ''} ${disabled ? 'disabled' : ''}`}
                    />
                </div>
                {dropdown && ReactDOM.createPortal(dropdown, document.body)}
            </div>
        </div>
    );
};

export default Select;
